/**
 * Off days, recorded in the Warehouse (Aziz, 2026-09-27). Meetings and shoots are only
 * offered at times the person is free, so an off day here keeps them out of both.
 *
 *  - on Profile: your own off days
 *  - on Team capacity (managers): everyone's, and a manager can add one for anyone
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarOff, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { addTimeOff, fetchTimeOff, offLabel, removeTimeOff, todayDubai } from '@/lib/availability';
import { fetchUsers } from '@/lib/queries';

function errText(e: unknown, fallback: string) {
  return (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? fallback;
}

export default function OffDaysCard({ team = false }: { team?: boolean }) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['time-off', team ? 'team' : 'me'], queryFn: () => fetchTimeOff(undefined, team ? 90 : 180) });
  const users = useQuery({ queryKey: ['users'], queryFn: fetchUsers, enabled: team });
  const [who, setWho] = useState('');
  const [from, setFrom] = useState(todayDubai());
  const [to, setTo] = useState('');
  const [hours, setHours] = useState(false);
  const [fromTime, setFromTime] = useState('10:00');
  const [toTime, setToTime] = useState('13:00');
  const [note, setNote] = useState('');

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['time-off'] });
    qc.invalidateQueries({ queryKey: ['free-times'] });
  };
  const add = useMutation({
    mutationFn: () => addTimeOff({
      user_id: team ? who || undefined : undefined, start_date: from,
      end_date: hours ? undefined : (to || undefined),
      from_time: hours ? fromTime : undefined, to_time: hours ? toTime : undefined, note: note.trim() || undefined,
    }),
    onSuccess: () => { toast.success('Off day recorded'); setNote(''); setTo(''); refresh(); },
    onError: (e) => toast.error(errText(e, 'Could not record it')),
  });
  const del = useMutation({
    mutationFn: removeTimeOff,
    onSuccess: () => { toast.success('Removed'); refresh(); },
    onError: (e) => toast.error(errText(e, 'Could not remove it')),
  });

  const rows = (list.data ?? []).filter((t) => team || t.mine);
  const people = (users.data ?? []).filter((u) => u.active && u.role !== 'client')
    .sort((a, b) => a.display_name.localeCompare(b.display_name))
    .map((u) => ({ value: u.id, label: u.display_name }));

  return (
    <Card className="mb-4" data-testid={team ? 'team-off-days' : 'my-off-days'}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-accent-600/20 text-accent-400 flex items-center justify-center shrink-0"><CalendarOff size={18} /></div>
        <div className="flex-1">
          <h3 className="font-medium text-muted-strong">{team ? 'Off days' : 'My off days'}</h3>
          <p className="text-xs text-muted mt-0.5">
            {team
              ? 'Who is away in the next 90 days. Meetings and shoots are never offered on these times.'
              : 'Days or hours you are away. Meetings and shoots are never offered to you on these times. The note is seen only by you and the managers.'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 items-end">
        {team && <Select label="Who" value={who} onChange={setWho} options={people} placeholder="Pick a person" searchable />}
        <Input label={hours ? 'Day' : 'First day'} type="date" value={from} min={todayDubai()} onChange={(e) => setFrom(e.target.value)} data-testid="off-from" />
        {hours ? (
          <div className="grid grid-cols-2 gap-2">
            <Input label="From" type="time" value={fromTime} onChange={(e) => setFromTime(e.target.value)} />
            <Input label="To" type="time" value={toTime} onChange={(e) => setToTime(e.target.value)} />
          </div>
        ) : (
          <Input label="Last day (optional)" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} data-testid="off-to" />
        )}
        <Input label="Note (optional)" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Annual leave" />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
        <label className="inline-flex items-center gap-2 text-xs text-muted">
          <input type="checkbox" checked={hours} onChange={(e) => setHours(e.target.checked)} /> Only some hours of one day
        </label>
        <Button size="sm" disabled={!from || (team && !who)} loading={add.isPending} onClick={() => add.mutate()} data-testid="off-add">
          Add off day
        </Button>
      </div>

      <ul className="mt-4 divide-y divide-slate-800/70">
        {rows.length === 0 && <li className="py-2 text-xs text-muted">{team ? 'Nobody is away in the next 90 days.' : 'No off days coming up.'}</li>}
        {rows.map((t) => (
          <li key={t.id} className="py-2 flex items-center gap-3 text-sm" data-testid="off-row">
            {team && <span className="w-40 shrink-0 truncate text-muted-strong">{t.name}</span>}
            <span className="flex-1 tabular-nums">{offLabel(t)}{t.note ? <span className="text-muted text-xs">  ·  {t.note}</span> : null}</span>
            {t.can_remove && (
              <button onClick={() => del.mutate(t.id)} className="p-1.5 rounded text-muted hover:text-red-300" title="Remove" aria-label="Remove this off day">
                <Trash2 size={14} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
